/**
 * Migration Utility: Populate Code IDs for Existing Objects
 * 
 * This utility generates and populates code_id for objects that don't have one yet.
 * It uses the folder path and object name to generate unique code IDs.
 */

import { apiService } from '../api-functions/api-service';
import { ObjectItem } from '../stores/objectsStore';
import { getFolderPath } from '../stores/folderStore';

export interface MigrationProgress {
  total: number;
  processed: number;
  successful: number;
  failed: number;
  errors: Array<{ objectId: string; objectName: string; error: string }>;
}

export interface MigrationResult {
  success: boolean;
  progress: MigrationProgress;
}

/**
 * Sanitize code ID to ensure it follows the required format
 */
function sanitizeCodeId(codeId: string): string {
  return codeId
    .toLowerCase()
    .replace(/[^a-z0-9/]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/\/_+|_+\//g, '/');
}

/**
 * Generate code ID from folder path and object name
 */
function generateCodeId(folderPath: string, objectName: string): string {
  // Sanitize folder path
  let path = folderPath;
  if (path.startsWith('/')) {
    path = path.substring(1); // Remove leading slash
  }
  
  if (!path) {
    path = 'root';
  }

  // Convert folder path to lowercase with forward slashes
  path = path
    .toLowerCase()
    .replace(/\//g, '/');

  // Sanitize object name
  const sanitizedName = objectName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

  return `${path}_${sanitizedName}`;
}

/**
 * Check if objects need code_id migration
 * @param gameId - The game ID to check
 * @returns Count of objects without code_id
 */
export async function checkMigrationNeeded(gameId: string): Promise<number> {
  try {
    const result = await apiService.fetchObjects(gameId);
    
    if (!result.success || !result.data) {
      throw new Error('Failed to fetch objects');
    }

    const objectsWithoutCodeId = result.data.filter(
      (obj: ObjectItem) => !obj.code_id || obj.code_id.trim() === ''
    );

    return objectsWithoutCodeId.length;
  } catch (error) {
    console.error('Error checking migration status:', error);
    throw error;
  }
}

/**
 * Populate code_id for all objects in a game that don't have one
 * @param gameId - The game ID to migrate
 * @param onProgress - Optional callback for progress updates
 * @returns Migration result with statistics
 */
export async function populateCodeIds(
  gameId: string,
  onProgress?: (progress: MigrationProgress) => void
): Promise<MigrationResult> {
  const progress: MigrationProgress = {
    total: 0,
    processed: 0,
    successful: 0,
    failed: 0,
    errors: []
  };

  try {
    // Fetch all objects for the game
    const result = await apiService.fetchObjects(gameId);
    
    if (!result.success || !result.data) {
      throw new Error('Failed to fetch objects');
    }

    // Filter objects that don't have code_id
    const objectsToMigrate = result.data.filter(
      (obj: ObjectItem) => !obj.code_id || obj.code_id.trim() === ''
    );

    progress.total = objectsToMigrate.length;

    if (progress.total === 0) {
      return { success: true, progress };
    }

    // Track used code IDs to avoid duplicates during migration
    const usedCodeIds = new Set<string>();
    
    // Add existing code IDs to the set
    result.data.forEach((obj: ObjectItem) => {
      if (obj.code_id) {
        usedCodeIds.add(obj.code_id);
      }
    });

    // Process each object
    for (const object of objectsToMigrate) {
      try {
        // Get folder path for this object
        const folderPath = object.folder 
          ? getFolderPath(gameId, 'library', object.folder)
          : '';

        // Generate code_id
        let generatedCodeId = generateCodeId(folderPath, object.name);
        
        // Ensure uniqueness by adding numeric suffix if needed
        let finalCodeId = generatedCodeId;
        let counter = 2;
        while (usedCodeIds.has(finalCodeId)) {
          finalCodeId = `${generatedCodeId}_${counter}`;
          counter++;
        }

        // Mark this code_id as used
        usedCodeIds.add(finalCodeId);

        // Update the object with the new code_id
        const updateResult = await apiService.updateObject({
          id: object.id,
          game_id: gameId,
          name: object.name,
          type: object.type || 'Library Object',
          data: object.data,
          tags: object.tags,
          blueprint_id: object.blueprint_id,
          version: object.version,
          blueprint_version: object.blueprint_version,
          identifiers: object.identifiers,
          folder: object.folder,
          status: object.status,
          owner: object.owner,
          edit_permissions: object.edit_permissions,
          description: object.description,
          code_id: finalCodeId,
          skipVersionBump: true // Don't increment version for migration
        });

        if (updateResult.success) {
          progress.successful++;
        } else {
          progress.failed++;
          progress.errors.push({
            objectId: object.id,
            objectName: object.name,
            error: 'Update failed'
          });
        }
      } catch (error) {
        progress.failed++;
        progress.errors.push({
          objectId: object.id,
          objectName: object.name,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }

      progress.processed++;
      
      // Call progress callback if provided
      if (onProgress) {
        onProgress({ ...progress });
      }
    }

    return {
      success: progress.failed === 0,
      progress
    };
  } catch (error) {
    console.error('Error during code ID migration:', error);
    throw error;
  }
}

/**
 * Generate a preview of code IDs that would be created
 * @param gameId - The game ID to preview
 * @returns Array of objects with their generated code IDs
 */
export async function previewCodeIds(
  gameId: string
): Promise<Array<{ objectId: string; objectName: string; generatedCodeId: string; folder: string }>> {
  try {
    const result = await apiService.fetchObjects(gameId);
    
    if (!result.success || !result.data) {
      throw new Error('Failed to fetch objects');
    }

    // Filter objects that don't have code_id
    const objectsToMigrate = result.data.filter(
      (obj: ObjectItem) => !obj.code_id || obj.code_id.trim() === ''
    );

    // Track used code IDs to show potential duplicates
    const usedCodeIds = new Set<string>();
    
    // Add existing code IDs to the set
    result.data.forEach((obj: ObjectItem) => {
      if (obj.code_id) {
        usedCodeIds.add(obj.code_id);
      }
    });

    // Generate preview for each object
    const previews = objectsToMigrate.map(object => {
      const folderPath = object.folder 
        ? getFolderPath(gameId, 'library', object.folder)
        : '';

      let generatedCodeId = generateCodeId(folderPath, object.name);
      
      // Check for duplicates and add suffix
      let finalCodeId = generatedCodeId;
      let counter = 2;
      while (usedCodeIds.has(finalCodeId)) {
        finalCodeId = `${generatedCodeId}_${counter}`;
        counter++;
      }

      // Mark this code_id as used for next iteration
      usedCodeIds.add(finalCodeId);

      return {
        objectId: object.id,
        objectName: object.name,
        generatedCodeId: finalCodeId,
        folder: folderPath || 'root'
      };
    });

    return previews;
  } catch (error) {
    console.error('Error generating code ID preview:', error);
    throw error;
  }
}
